/**
 * Frontend auth glue.
 *
 *  - `AuthProvider` wraps the app, fetches /api/auth/me on mount, holds the admin in context.
 *  - `useAuth()` exposes { admin, loading, refresh, logout }.
 *  - `ProtectedRoute` redirects unauthenticated visitors to /login.
 *
 * All fetches go to the Worker via VITE_API_BASE with `credentials: 'include'`
 * so the cross-site session cookie rides along.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';

export const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export type Admin = {
  id: string;
  email: string;
  name: string | null;
};

type AuthContextValue = {
  admin: Admin | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = (await res.json()) as { ok: boolean; admin: Admin };
        setAdmin(data.admin ?? null);
      } else {
        setAdmin(null);
      }
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Even if the request fails, we still clear local state below.
    }
    setAdmin(null);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ admin, loading, refresh, logout }),
    [admin, loading, refresh, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Wrap any route that requires a logged-in admin.
 * While the initial /api/auth/me check is in flight, renders a tiny loading state
 * so we don't flash-of-login-page.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { admin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center bg-cream-soft text-burgundy-faint font-hanken text-sm">
        Checking session...
      </div>
    );
  }

  if (!admin) {
    // Preserve where they were headed so /login can bounce them back after sign-in.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
